import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Simple beep via WebAudio
function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    o.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    o.start();
    o.stop(ctx.currentTime + 0.42);
  } catch {}
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(new Set());
  const first = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      const newOnes = data.notifications.filter((n) => !seen.current.has(n.id));
      if (!first.current) {
        newOnes.forEach((n) => {
          if (!n.read) {
            beep();
            toast.success(n.message, { duration: 8000 });
          }
        });
      }
      newOnes.forEach((n) => seen.current.add(n.id));
      setItems(data.notifications);
      setUnread(data.unread);
      first.current = false;
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAll = async () => {
    await api.post("/admin/notifications/read-all");
    load();
  };

  return (
    <div className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-md border border-zinc-800 bg-neutral-900 p-2 hover:bg-neutral-800"
      >
        <Bell size={16} strokeWidth={1.5} />
        {unread > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-orange-600 px-1 text-[10px] font-bold text-white"
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-80 rounded-lg border border-zinc-800 bg-neutral-950 p-2 shadow-2xl">
          <div className="flex items-center justify-between px-2 py-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Notifications</p>
            <button onClick={markAll} className="text-[11px] text-stone-700 hover:underline">Mark all read</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <p className="p-4 text-center text-xs text-zinc-500">No notifications yet</p>}
            {items.map((n) => (
              <div key={n.id} className={`rounded-md px-2 py-2 text-xs ${n.read ? "text-zinc-500" : "bg-stone-800/50 text-white"}`}>
                <p>{n.message}</p>
                <p className="mt-0.5 text-[10px] text-zinc-500 font-mono">{new Date(n.at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
